/**
 * Formata um valor monetário em Kwanzas (Kz)
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-AO', {
    style: 'currency',
    currency: 'AOA',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Formata um valor monetário sem símbolo da moeda
 */
export const formatCurrencyValue = (value: number): string => {
  return new Intl.NumberFormat('pt-AO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

// Fuso horário oficial da plataforma. Angola é UTC+1 (sem horário de verão).
// Fixamos SEMPRE este fuso para que a hora exibida não dependa do fuso do
// dispositivo/servidor (ex.: build/SSR em UTC mostrava a hora 1h atrasada).
// Ponto único de verdade — não formatar datas com hora fora daqui.
export const TIMEZONE = 'Africa/Luanda'

// Período experimental: um prestador é "Novo" durante os primeiros ~6 meses
// desde o registo (created_at). Alterar aqui muda em toda a app.
export const NOVO_PRESTADOR_DIAS = 180
export const isNovoPrestador = (iso?: string): boolean => {
  if (!iso) return false
  const d = new Date(iso)
  if (isNaN(d.getTime())) return false
  return (Date.now() - d.getTime()) / 86400000 <= NOVO_PRESTADOR_DIAS
}

/**
 * Há quanto tempo (e desde quando) o prestador está na plataforma, a partir
 * de created_at. Devolve { ha: "há 2 meses", desde: "julho de 2026" }.
 */
export const formatMembroDesde = (iso?: string): { desde: string; ha: string } => {
  if (!iso) return { desde: '—', ha: '' }
  const d = new Date(iso)
  if (isNaN(d.getTime())) return { desde: '—', ha: '' }
  const desde = new Intl.DateTimeFormat('pt-AO', { month: 'long', year: 'numeric', timeZone: TIMEZONE }).format(d)
  const dias = Math.floor((Date.now() - d.getTime()) / 86400000)
  let ha: string
  if (dias < 1) ha = 'hoje'
  else if (dias < 30) ha = `há ${dias} dia${dias === 1 ? '' : 's'}`
  else if (dias < 365) { const m = Math.floor(dias / 30); ha = `há ${m} ${m === 1 ? 'mês' : 'meses'}` }
  else { const a = Math.floor(dias / 365); ha = `há ${a} ano${a === 1 ? '' : 's'}` }
  return { desde, ha }
}

/**
 * Formata uma data para exibição (sem hora)
 */
export const formatDate = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return ''
  return new Intl.DateTimeFormat('pt-AO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: TIMEZONE,
  }).format(dateObj)
}

/**
 * Formata uma data e hora para exibição
 */
export const formatDateTime = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return ''
  return new Intl.DateTimeFormat('pt-AO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIMEZONE,
  }).format(dateObj)
}

/**
 * Formata apenas a hora (HH:MM) — usar em chat, listas de atividade, etc.
 */
export const formatTime = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return ''
  return new Intl.DateTimeFormat('pt-AO', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIMEZONE,
  }).format(dateObj)
}

/**
 * Formata um número para exibição com separadores de milhares
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('pt-AO').format(value)
}

/**
 * Formata um número de telefone
 */
export const formatPhone = (phone: string): string => {
  // Remove todos os caracteres não numéricos
  const cleaned = phone.replace(/\D/g, '')
  
  // Formata baseado no tamanho
  if (cleaned.length === 9) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`
  }
  
  return phone
}

/**
 * Trunca um texto com reticências
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

/**
 * Formata um status para exibição
 */
export const formatStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    active: 'Ativo',
    inactive: 'Inativo',
    expired: 'Expirado',
    cancelled: 'Cancelado',
    pending: 'Pendente',
    completed: 'Concluído',
    failed: 'Falhou',
  }
  
  return statusMap[status] || status
} 